// @ts-strict-ignore
import type {
  FieldValues, Path, RegisterOptions, UseFormRegisterReturn,
} from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

/**
 * Builds a custom `register` object builder that has custom `onChange` handler
 * that automatically prevents a field from being an empty string.
 * This is meant to be used once per form & it is optimized so no state change
 * updates occur.
 *
 * @returns {Function} A field registration function that prevents empty fields
 * @example
 * const preventEmptyRegister = usePreventEmptyRegister();
 * return (
 *  <div>
 *    <input {...preventEmptyRegister('fieldA')} />
 *    <input {...preventEmptyRegister('fieldB')} />
 * </div>
 * )
 *
 */
export default function usePreventEmptyRegister<T extends FieldValues>() {
  const { register, getValues } = useFormContext<T>();

  // closured handler that returns the custom & up-to-date `register` object
  return (name: Path<T>, options?: RegisterOptions<T, Path<T>>): UseFormRegisterReturn => {
    // grabs the immediate form values before the `onChange` happened
    const prevValues = getValues();
    const { onChange: defaultOnChange, ...rest } = register(name, options);
    return {
      ...rest,
      onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
          /**
           * manually update the event's change value to the one before the event happened,
           * rejecting the incoming empty string value for that specific form field.
           * this sets the field's value to the last value before the empty string
           */
          e.target.value = prevValues[name];
        } else {
          defaultOnChange(e);
        }
      },
    };
  };
}
